import React, {useState} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';

import Format from '~/lib/format';

import sy from '~/styles';
import {Text, Pressable, Grouping, ActionButton} from '~/components/controls';
import Actionsheet, {Title, Actions} from '~/components/controls/Actionsheet';
import withDimensions from '~/components/hoc/with-dimensions';

import AcUnitIcon from '~/images/md-icons/ac_unit/materialicons/24px.svg';
import SunnyIcon from '~/images/md-icons/sunny/materialicons/24px.svg';
import SunnySnowingIcon from '~/images/md-icons/sunny_snowing/materialicons/24px.svg';
import ListIcon from '~/images/md-icons/list/materialicons/24px.svg';
import DeleteIcon from '~/images/md-icons/delete/materialicons/24px.svg';
import TireRepairIcon from '~/images/md-icons/tire_repair/materialicons/24px.svg';
import ArchiveIcon from '~/images/md-icons/archive/materialicons/24px.svg';
import UnarchiveIcon from '~/images/md-icons/unarchive/materialicons/24px.svg';
import QuestionMarkIcon from '~/images/md-icons/question_mark/materialicons/24px.svg';
import ThumbDownIcon from '~/images/md-icons/thumb_down/materialicons/24px.svg';
import ThumbDownOutlinedIcon from '~/images/md-icons/thumb_down/materialiconsoutlined/24px.svg';

import {SeasonIndicator, Profile} from './shared';

const TIRE_LOCATION_ORDER = ['LV', 'RV', 'LA', 'RA', 'X1'];

const TIRE_SEASON_INDICATOR = {
  Summer: 'Summer',
  Winter: 'Winter',
  AllSeason: 'AllSeason',
};

const TIRE_SOURCE = {
  New: 'New',
  Stored: 'Stored',
  Reserve: 'Reserve',
  Used: 'Used',
  LessorStock: 'LessorStock',
  ThirdParty: 'ThirdParty',
  Private: 'Private',
};

const HEADER_HEIGHT_IN_PX = 70;
const FOOTER_HEIGHT_IN_PX = 50;
const DIVIDER_HEIGHT_IN_PX = 1;

const TiresTitle = ({seasonIndicators, textStyle, children}) => {
  let caption = 'Huidige banden';
  let icon = null;

  if (seasonIndicators.length > 0) {
    if (
      seasonIndicators.every((item) => item === TIRE_SEASON_INDICATOR.Summer)
    ) {
      caption = 'Zomerbanden';
      icon = <SunnyIcon fill="#FFB800" style={sy['pr-8']} />;
    } else if (
      seasonIndicators.every((item) => item === TIRE_SEASON_INDICATOR.Winter)
    ) {
      caption = 'Winterbanden';
      icon = <AcUnitIcon fill="#49B3FF" style={sy['pr-8']} />;
    } else if (
      seasonIndicators.every((item) => item === TIRE_SEASON_INDICATOR.AllSeason)
    ) {
      caption = 'All-season banden';
      icon = <SunnySnowingIcon fill="#997A29" style={sy['pr-8']} />;
    }
  } else if (seasonIndicators.length === 0) {
    icon = <QuestionMarkIcon fill="#FF6C00" style={sy['pr-8']} />;
  }

  return (
    <View
      style={[
        sy['flex-row'],
        sy['justify-between'],
        sy['items-center'],
        sy['flex-1'],
        {
          width: '100%',
        },
      ]}>
      <View style={[sy['flex-row'], sy['items-center']]}>
        {icon}
        <Text style={textStyle}>
          {caption}
          {children}
        </Text>
      </View>
      <View style={[sy['flex-row']]}></View>
    </View>
  );
};

const TireIcon = ({action, ...rest}) => {
  let icon;
  switch (action) {
    case 'Current_Replace':
    case 'Current_Remove':
    case 'Stored_ReplaceNew':
      icon = <DeleteIcon {...rest} />;
      break;
    case 'Current_Store':
    case 'Current_StoreWheel':
      icon = <ArchiveIcon {...rest} />;
      break;
    case 'Current_TireRepair':
      icon = <TireRepairIcon {...rest} />;
      break;
    case 'Stored_WheelChange':
    case 'Stored_TireChange':
    case 'Stored_TireChangeThirdParty':
      icon = <UnarchiveIcon {...rest} />;
      break;
    default:
      icon = <ListIcon {...rest} />;
      break;
  }

  return icon;
};

const Tires = ({tires}) => {
  const profile_widths = tires.reduce((acc, item) => {
    acc[item.location] = item.attributes?.profile_width;
    return acc;
  }, {});

  return tires.map((tire, index, array) => {
    const {location, action, attributes} = tire;
    const {rob_tire_id, title, description} = attributes;

    const is_last = index === array.length - 1;

    return (
      <View style={[sy['flex-row']]} key={location}>
        <Profile
          style={[sy['p-4'], sy['pr-8']]}
          location={tire.location}
          widths={profile_widths}
        />
        <View style={[sy['flex-1']]}>
          <View style={[sy['p-4'], sy['pl-0']]}>
            <View style={[sy['flex-row'], sy['justify-between']]}>
              <View style={[sy['flex-1']]}>
                <Text>
                  {location}&nbsp;&middot;&nbsp;
                  {rob_tire_id ? title : 'Onbekend'}
                </Text>
                <Text
                  style={[sy.smallRegular, sy['text-lightgray'], sy.truncate]}>
                  {rob_tire_id ? description : 'Niet opgegeven'}
                </Text>
              </View>
              {rob_tire_id && (
                <TireIcon action={action} width={24} height={24} />
              )}
            </View>
          </View>
          {!is_last && <Divider style={[{backgroundColor: '#f2f2f2'}]} />}
        </View>
      </View>
    );
  });
};

const NewTires = ({tires}) => {
  const profile_widths = tires.reduce((acc, item) => {
    acc[item.location] = item.profile_width;
    return acc;
  }, {});

  return tires.map((tire, index, array) => {
    const {
      location,
      title,
      description,
      source,
      price,
      discount,
      seasonIndicator,
    } = tire;

    const is_last = index === array.length - 1;

    return (
      <View style={[sy['flex-row']]} key={location}>
        <Profile
          style={[sy['p-4'], sy['pr-8']]}
          location={location}
          widths={profile_widths}
        />
        <View style={[sy['flex-1']]}>
          <View style={[sy['p-4'], sy['pl-0']]}>
            <View
              style={[
                sy['flex-row'],
                sy['justify-between'],
                sy['items-center'],
              ]}>
              <View style={[sy['flex-1']]}>
                <Text>
                  {location}&nbsp;&middot;&nbsp;{title}
                </Text>
                <Text
                  style={[sy.smallRegular, sy['text-lightgray'], sy.truncate]}>
                  {description}
                </Text>
              </View>
              <View style={[sy['pl-2']]}>
                <Text style={sy.smallRegular}>
                  {[
                    TIRE_SOURCE.Reserve,
                    TIRE_SOURCE.LessorStock,
                    TIRE_SOURCE.ThirdParty,
                    TIRE_SOURCE.Private,
                  ].includes(source)
                    ? null
                    : Format.price(price - discount)}
                </Text>
                <View style={[sy['flex-row'], sy['justify-end']]}>
                  <SeasonIndicator indicator={seasonIndicator} />
                </View>
              </View>
            </View>
          </View>
          {!is_last && <Divider style={[{backgroundColor: '#f2f2f2'}]} />}
        </View>
      </View>
    );
  });
};

const TiresActionsheet = ({
  dimensions,
  task,
  is_denied,
  onDismiss,
  onChange,
}) => {
  const [selected] = useState(
    task.parts.reduce((acc, item) => {
      const {
        mounted: attributes,
        stored,
        repairs,
        ...new_tire
      } = item.attributes;

      acc[item.location] = {
        action: item.action,
        attributes,
        stored: stored ?? {},
        repairs,
        new_tire: new_tire?.new_rob_tire_id
          ? {
              ...new_tire,
              description: item.description,
              discount: item.discount,
              price: item.price,
              title: item.number,
            }
          : null,
      };

      return acc;
    }, {}),
  );
  const [taskDenied, setTaskDenied] = useState(is_denied);

  const onOK = async () => {
    await onChange(taskDenied);
    onDismiss();
  };

  const mounted = Object.keys(selected)
    .sort(
      (a, b) => TIRE_LOCATION_ORDER.indexOf(a) - TIRE_LOCATION_ORDER.indexOf(b),
    )
    .map((location) => ({
      location,
      action: selected[location].action,
      attributes: selected[location].attributes,
    }));

  const stored = Object.keys(selected)
    .sort(
      (a, b) => TIRE_LOCATION_ORDER.indexOf(a) - TIRE_LOCATION_ORDER.indexOf(b),
    )
    .filter((location) => selected[location].stored.action)
    .map((location) => ({
      location,
      action: selected[location].stored.action,
      attributes: selected[location].stored.attributes,
    }));

  const new_tires = Object.keys(selected)
    .sort(
      (a, b) => TIRE_LOCATION_ORDER.indexOf(a) - TIRE_LOCATION_ORDER.indexOf(b),
    )
    .filter((location) => selected[location].new_tire)
    .map((location) => ({
      location,
      ...selected[location].new_tire,
    }));

  return (
    <>
      <Actionsheet visible={true} onDismiss={onDismiss}>
        <Title>
          <TiresTitle
            textStyle={[sy.largePlus, {lineHeight: 24}]}
            seasonIndicators={mounted
              .map((tire) => tire.attributes?.seasonIndicator)
              .filter(Boolean)}
          />
          <Pressable onPress={() => setTaskDenied(!taskDenied)}>
            {taskDenied ? (
              <ThumbDownIcon fill="#972727" width={24} height={24} />
            ) : (
              <ThumbDownOutlinedIcon fill="#972727" width={24} height={24} />
            )}
          </Pressable>
        </Title>
        <Divider />
        <ScrollView
          contentContainerStyle={{
            maxHeight:
              dimensions.window.height -
              HEADER_HEIGHT_IN_PX -
              DIVIDER_HEIGHT_IN_PX -
              FOOTER_HEIGHT_IN_PX -
              DIVIDER_HEIGHT_IN_PX,
          }}>
          <Tires tires={mounted} />
          {new_tires.length > 0 && (
            <Grouping
              header={
                <TiresTitle
                  textStyle={sy.mediumBold}
                  seasonIndicators={new_tires.map(
                    (tire) => tire.seasonIndicator,
                  )}>
                  &nbsp;&middot;&nbsp;Nieuw
                </TiresTitle>
              }
              initial={{expand: true}}>
              <NewTires tires={new_tires} />
            </Grouping>
          )}
          {stored?.length > 0 && (
            <Grouping
              header={
                <TiresTitle
                  textStyle={sy.mediumBold}
                  seasonIndicators={stored
                    .map((tire) => tire.attributes?.seasonIndicator)
                    .filter(Boolean)}>
                  &nbsp;&middot;&nbsp;In opslag
                </TiresTitle>
              }
              initial={{expand: true}}>
              <Tires tires={stored} />
            </Grouping>
          )}
        </ScrollView>
        <Divider />
        <Actions>
          <ActionButton onPress={onDismiss}>Annuleren</ActionButton>
          <ActionButton onPress={onOK}>OK</ActionButton>
        </Actions>
      </Actionsheet>
    </>
  );
};

export default withDimensions(TiresActionsheet);
